<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import DatePicker from 'vue-datepicker-next';
import addDays from 'date-fns/addDays';
import LeadTasksAPI from 'dashboard/api/masifi/leadTasks';
import LeadTaskTypesAPI from 'dashboard/api/masifi/leadTaskTypes';
import LeadsAPI from 'dashboard/api/masifi/leads';
import { unwrap, DATE_PICKER_LANG_ES } from '../helper';

// Diálogo reutilizable de tarea (alta/edición/completar). Lo usan la ficha del
// lead (LeadTasksPanel, lead fijo vía openNew(leadId)) y la Agenda global
// (leadPicker = true → ComboBox con búsqueda de leads por API).
const props = defineProps({
  assigneeOptions: { type: Array, default: () => [] },
  leadPicker: { type: Boolean, default: false },
});
const emit = defineEmits(['saved', 'completed', 'deleted']);

const { t, locale } = useI18n();
const store = useStore();

// Locale es como OBJETO por el prop :lang (derivado de date-fns en helper.js;
// ahí está el porqué de NO importar 'vue-datepicker-next/locale/es').
const datePickerLang = computed(() =>
  locale.value?.startsWith('es') ? DATE_PICKER_LANG_ES : 'en'
);

// No permitir fechas anteriores a hoy (paridad con el DateTimePicker nativo).
const disableBeforeToday = date => date < addDays(new Date(), -1);

const types = ref([]);
const formDialog = ref(null);
const completeDialog = ref(null);
const completing = ref(null);
const resultText = ref('');
const leadOptions = ref([]);

const form = reactive({
  id: null,
  leadId: null,
  title: '',
  leadTaskTypeId: '',
  dueAt: null,
  assigneeId: null,
});

const loadTypes = async () => {
  try {
    const { data } = await LeadTaskTypesAPI.get();
    types.value = unwrap(data);
  } catch (error) {
    types.value = [];
  }
};
onMounted(loadTypes);

const typeOptions = computed(() => [
  { value: '', label: t('LEADS.TASK.NO_TYPE') },
  ...types.value.map(ty => ({ value: ty.id, label: ty.name })),
]);

const dialogTitle = computed(() =>
  form.id ? t('LEADS.TASK.EDIT_TITLE') : t('LEADS.TASK.NEW_TITLE')
);

const refreshBadge = () => {
  const userId = store.getters.getCurrentUser?.id;
  if (userId) store.dispatch('agenda/fetchStats', { assignee_id: userId });
};

const resetForm = (leadId = null) => {
  form.id = null;
  form.leadId = leadId;
  form.title = '';
  form.leadTaskTypeId = '';
  form.dueAt = null;
  form.assigneeId = null;
};

const openNew = (leadId = null) => {
  resetForm(leadId);
  // La tarea nace asignada a quien la crea (patrón Kommo): si naciera "Sin
  // asignar", con el preset "Mis tareas" la tarea recién guardada no aparece
  // en la lista ni suma al badge — parece perdida. Guard: si el picker se
  // muestra pero el usuario no está entre las opciones (agents aún cargando),
  // NO defaultear a un valor que el Select renderizaría en blanco.
  const currentUserId = store.getters.getCurrentUser?.id ?? null;
  const optionsCanShowUser =
    !props.assigneeOptions.length ||
    props.assigneeOptions.some(option => option.value === currentUserId);
  form.assigneeId = optionsCanShowUser ? currentUserId : null;
  leadOptions.value = [];
  formDialog.value?.open();
};

const openEdit = task => {
  form.id = task.id;
  form.leadId = task.lead_id;
  form.title = task.title || '';
  form.leadTaskTypeId = task.lead_task_type_id ?? '';
  form.dueAt = task.due_at ? new Date(task.due_at) : null;
  form.assigneeId = task.assignee_id ?? null;
  // Precargar el nombre del lead para mostrarlo en el ComboBox al editar.
  if (props.leadPicker && task.lead) {
    leadOptions.value = [{ value: task.lead.id, label: task.lead.name }];
  }
  formDialog.value?.open();
};

const searchLeads = async query => {
  if (!query || query.length < 2) return;
  try {
    const { data } = await LeadsAPI.get({ search: query });
    leadOptions.value = unwrap(data).map(lead => ({
      value: lead.id,
      label: lead.name,
    }));
  } catch (error) {
    leadOptions.value = [];
  }
};

const save = async () => {
  if (!form.title.trim()) {
    useAlert(t('LEADS.TASK.NAME_REQUIRED'));
    return;
  }
  if (!form.dueAt) {
    useAlert(t('LEADS.TASK.DUE_REQUIRED'));
    return;
  }
  if (!form.leadId) {
    useAlert(t('LEADS.TASK.LEAD_REQUIRED'));
    return;
  }
  const payload = {
    lead_task: {
      lead_id: form.leadId,
      title: form.title.trim(),
      lead_task_type_id: form.leadTaskTypeId,
      due_at: form.dueAt,
      assignee_id: form.assigneeId,
    },
  };
  try {
    if (form.id) await LeadTasksAPI.update(form.id, payload);
    else await LeadTasksAPI.create(payload);
    formDialog.value?.close();
    useAlert(t('LEADS.TASK.SAVED'));
    refreshBadge();
    emit('saved');
  } catch (error) {
    useAlert(t('LEADS.TASK.SAVE_ERROR'));
  }
};

const openComplete = task => {
  completing.value = task;
  resultText.value = '';
  completeDialog.value?.open();
};

const confirmComplete = async () => {
  if (!completing.value) return;
  try {
    await LeadTasksAPI.complete(completing.value.id, {
      result_text: resultText.value,
    });
    completeDialog.value?.close();
    useAlert(t('LEADS.TASK.COMPLETED_TOAST'));
    refreshBadge();
    emit('completed');
  } catch (error) {
    useAlert(t('LEADS.TASK.COMPLETE_ERROR'));
  }
};

const remove = async task => {
  try {
    await LeadTasksAPI.delete(task.id);
    useAlert(t('LEADS.TASK.DELETED'));
    refreshBadge();
    emit('deleted');
  } catch (error) {
    useAlert(t('LEADS.TASK.DELETE_ERROR'));
  }
};

defineExpose({ openNew, openEdit, openComplete, remove });
</script>

<template>
  <div>
    <!-- Alta / edición -->
    <Dialog
      ref="formDialog"
      :title="dialogTitle"
      :confirm-button-label="t('LEADS.TASK.SAVE')"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <div v-if="leadPicker" class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('LEADS.TASK.LEAD') }}
          </span>
          <ComboBox
            v-model="form.leadId"
            :options="leadOptions"
            use-api-results
            :placeholder="t('LEADS.TASK.SEARCH_LEAD')"
            :search-placeholder="t('LEADS.TASK.SEARCH_LEAD')"
            @search="searchLeads"
          />
        </div>
        <Input
          v-model="form.title"
          :label="t('LEADS.TASK.NAME')"
          :placeholder="t('LEADS.TASK.NAME_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('LEADS.TASK.TYPE') }}
          </span>
          <Select
            v-model="form.leadTaskTypeId"
            class="!w-full"
            :options="typeOptions"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('LEADS.TASK.DUE_AT') }}
          </span>
          <!-- append-to-body=false: el popup se renderiza DENTRO del <dialog>
               (top layer del navegador); si se teletransporta a body queda
               detrás del modal sin importar el z-index. -->
          <div class="date-picker">
            <DatePicker
              type="datetime"
              confirm
              :clearable="false"
              :editable="false"
              :append-to-body="false"
              :lang="datePickerLang"
              :confirm-text="t('LEADS.TASK.SAVE')"
              :value="form.dueAt"
              :disabled-date="disableBeforeToday"
              @change="form.dueAt = $event"
            />
          </div>
        </div>
        <div v-if="assigneeOptions.length" class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('LEADS.TASK.ASSIGNEE') }}
          </span>
          <Select
            v-model="form.assigneeId"
            class="!w-full"
            :options="assigneeOptions"
          />
        </div>
      </div>
    </Dialog>

    <!-- Completar -->
    <Dialog
      ref="completeDialog"
      :title="t('LEADS.TASK.COMPLETE_TITLE')"
      :confirm-button-label="t('LEADS.TASK.COMPLETE')"
      @confirm="confirmComplete"
    >
      <TextArea
        v-model="resultText"
        :label="t('LEADS.TASK.RESULT')"
        :placeholder="t('LEADS.TASK.RESULT_PLACEHOLDER')"
        :max-length="2000"
        auto-height
        resize
      />
    </Dialog>
  </div>
</template>
